/**
 * Format: Media
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API media responses.
 *
 */
import { formatImage } from '~/format/format-image';
import { decodeApiId, transformEdges } from '~/helpers/graphql';

/**
 * Format media.
 * - https://shopify.dev/docs/api/storefront/2024-01/interfaces/Media
 * @param {Object} response - GraphQL response.
 * @param {Boolean} isFromProduct - Whether the media is from a product response
 * @param {Object} product - Product from which the response comes from.
 * @returns {Array}
 */
export function formatMedia(response, isFromProduct = true, product) {
  let mediaArray = [];

  if (isFromProduct) {
    mediaArray = transformEdges(response.media);
  } else {
    mediaArray = transformEdges(response);
  }

  if (!mediaArray.length) {
    return [];
  }

  return mediaArray.map((media, index) => {
    const formattedMedia = {
      alt: media.alt,
      id: decodeApiId(media.id) ?? false,
      index,
      mediaType: media.mediaType.toLowerCase(),
    };

    /**
     * Match variant to media if it exists.
     */
    const formattedImageObject =
      formattedMedia.mediaType === 'image'
        ? formatImage(media.image)
        : formatImage(media.previewImage);

    const matchedVariant = product
      ? transformEdges(product.variants).find((variant) => {
          return variant.image?.src === formattedImageObject.src;
        })
      : null;

    formattedMedia.variant = decodeApiId(matchedVariant?.id) ?? false;

    /**
     * Add additional media properties.
     */
    switch (formattedMedia.mediaType) {
      case 'external_video':
        formattedMedia.embedUrl = media.embedUrl;
        formattedMedia.host = media.host;
        formattedMedia.previewImage = formattedImageObject;
        break;

      case 'image':
        formattedMedia.image = formattedImageObject;
        break;

      case 'model_3d':
        formattedMedia.previewImage = formattedImageObject;

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            mimeType: source.mimeType,
            url: source.url,
          };
        });
        break;

      case 'video':
        formattedMedia.previewImage = formattedImageObject;

        formattedMedia.sources = media.sources.map((source) => {
          return {
            format: source.format,
            height: source.height,
            mimeType: source.mimeType,
            url: source.url,
            width: source.width,
          };
        });
        break;
    }

    return formattedMedia;
  });
}
